const MIN_GRID_DIMENSION = 1;
const MAX_GRID_DIMENSION = 8192;
const DEFAULT_TARGET_CELL_SIZE_CSS_PX = 8;
const DEFAULT_QUANTUM = 32;

export type LightningGridResolution = Readonly<{ width: number; height: number }>;

export type LightningGridResolutionInput = {
  viewportWidthCss: number;
  viewportHeightCss: number;
  coverageScaleX?: number;
  coverageScaleY?: number;
  targetCellSizeCssPx?: number;
  quantum?: number;
  testOverride?: LightningGridResolution;
};

function clampDimension(value: number): number {
  return Math.max(MIN_GRID_DIMENSION, Math.min(MAX_GRID_DIMENSION, value));
}

function validateOverride(override: LightningGridResolution): LightningGridResolution {
  if (
    !Number.isInteger(override.width) ||
    !Number.isInteger(override.height) ||
    override.width < MIN_GRID_DIMENSION ||
    override.height < MIN_GRID_DIMENSION ||
    override.width > MAX_GRID_DIMENSION ||
    override.height > MAX_GRID_DIMENSION
  ) {
    throw new RangeError(`Lightning grid override must be integer dimensions within 1..${MAX_GRID_DIMENSION}`);
  }
  return Object.freeze({ ...override });
}

/**
 * Resolves a CSS-pixel grid. Device pixel ratio is intentionally not applied
 * here; it is applied once when the shader expands icons into framebuffer pixels.
 */
export function resolveLightningGridResolution(input: LightningGridResolutionInput): LightningGridResolution {
  if (input.testOverride) {
    return validateOverride(input.testOverride);
  }

  const targetCellSize = input.targetCellSizeCssPx ?? DEFAULT_TARGET_CELL_SIZE_CSS_PX;
  const quantum = input.quantum ?? DEFAULT_QUANTUM;
  if (!Number.isFinite(targetCellSize) || targetCellSize <= 0) {
    throw new RangeError("Lightning target cell size must be positive");
  }
  if (!Number.isInteger(quantum) || quantum <= 0) {
    throw new RangeError("Lightning grid quantum must be a positive integer");
  }

  const resolve = (viewportSize: number, coverageScale: number): number => {
    const safeSize = Number.isFinite(viewportSize) ? Math.max(1, viewportSize) : 1;
    const safeScale = Number.isFinite(coverageScale) ? Math.max(1, coverageScale) : 1;
    const raw = Math.ceil((safeSize * safeScale) / targetCellSize);
    return clampDimension(Math.ceil(raw / quantum) * quantum);
  };

  return Object.freeze({
    width: resolve(input.viewportWidthCss, input.coverageScaleX ?? 1),
    height: resolve(input.viewportHeightCss, input.coverageScaleY ?? 1),
  });
}
